import { isNative, getPlatform, isAndroid, isIos } from 'src/utils/platform';
import { env } from 'src/config/env';
import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { App as AntApp, ConfigProvider } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/es/locale/en_US';
import { initSentry, onReactCaughtError, reportError } from 'src/modules/logs/Sentry';
import posthog from 'posthog-js';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';

import './App.css';
import i18n, { initTranslation, getAntdLocale } from 'src/modules/i18n';
import { queryClient } from 'src/queries/base';
import BootHealthGate from 'src/components/RenderHealthProbe/BootHealthGate';
import PageSpinner from 'src/designSystem/PageSpinner';
import { antdTheme } from 'src/styles/theme';

import 'unfonts.css';
import 'src/styles/preflight.css';
import 'src/styles/main.css';
import NotificationContextProvider from './contexts/NotificationContext';
import { initAnalytics } from 'src/modules/analytics/analytics';
import { initPosthog } from 'src/modules/analytics/posthogClient';
import { posthogBootConsent } from 'src/funnelWeb/consent/consentStore';
import { SafeArea, SystemBarsStyle } from '@capacitor-community/safe-area';
import { initAxios } from 'src/modules/axios';
import { SplashScreen } from 'src/modules/splash-screen';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { initInAppPurchase } from 'src/modules/purchase/RevenueCat';
import { initSupport } from 'src/modules/support/support';
import DebugShakeListener from 'src/components/ShakeListener/DebugShakeListener';
import TikTokPixel from 'src/modules/analytics/TikTokPixel';
import { initPushNotifications } from 'src/modules/push-notifications/push-notifications';
import { resetLegacyAppIcon } from 'src/modules/legacy-app-icon';
import { App as CapacitorApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import AppsFlyer from './modules/AppsFlyer/AppsFlyer';
import {
  registerAppsFlyerDeepLinkListener,
  initAppsFlyerSdk,
  startAppsFlyerSdk,
} from 'src/modules/AppsFlyer/AppsFlyer.module';
import { requestATTPermission } from 'src/modules/analytics/ATT';
import { enableFacebookTrackingIfUnconfigured } from 'src/modules/analytics/FacebookSDK';
import PreviewBadge from 'src/components/BuildInfo/PreviewBadge';
import {
  detectOrphanedCameraSession,
  buildCameraRedirectPath,
  setRestoredCameraPhotos,
  setPendingCameraRedirect,
  type RestoredPhotoScope,
} from 'src/modules/camera-session';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import {
  createCameraRestoreTracker,
  type RestoredCameraResult,
} from 'src/modules/boot/cameraRestore';
import { setBootPhase } from 'src/modules/boot/bootPhase';
import {
  clearCrashState,
  installBackgroundClear,
  readCrashState,
  recordLaunch,
} from 'src/modules/boot/crashRecovery';
import {
  armRenderProbe,
  clearRenderHealth,
  noteActiveBundleId,
  readRenderHealth,
} from 'src/modules/boot/renderHealth';
import { reportChunkFailure } from 'src/modules/boot/chunkFailure';
import { readDeviceDiagnostics, toConnectionType } from 'src/modules/boot/deviceDiagnostics';
import {
  emitPendingRevertReport,
  persistRevertReport,
} from 'src/modules/boot/revertReport';
import {
  LOCAL_INIT_TIMEOUT_MS,
  runLocalInit,
} from 'src/modules/boot/localInit';
import { armBootWatchdog } from 'src/modules/boot/bootWatchdog';
import { runNonCriticalInit } from 'src/modules/boot/nonCriticalInit';
import { runPostRenderTail } from 'src/modules/boot/postRenderTail';
import {
  gatherUpdateInput as gatherUpdateInputRaw,
  getCurrentBundleId,
  getCurrentBundleVersion,
} from 'src/modules/boot/updateCheck.io';
import {
  createProgressiveHintStore,
  makeCleanupFailedBundles,
  makeIsProgressiveTrigger,
} from 'src/modules/boot/progressiveSupport';
import {
  clearCrashStateIfLocalInitSucceeded,
  emitFastTrackUpdateTelemetry,
  pickBootView,
  runBootSequence,
  runCrashRecovery,
  type BootSequenceDeps,
  type GateState,
} from 'src/modules/boot/bootSequence';
import UpdateGate from 'src/components/UpdateGate/UpdateGate';
import { resolveGateLocale } from 'src/components/UpdateGate/utils/resolveGateLocale';
import BootPlaceholder from 'src/components/Boot/BootPlaceholder';
import { splashRelease } from 'src/modules/boot/splashRelease';
import { listenForCapgoDownloadFailed } from 'src/modules/boot/capgoDownloadFailed';
import { withTimeout } from 'src/utils/withTimeout';
import { redirectReferralVisitToStore } from 'src/utils/referralStoreLink';
import { selectAppTree } from 'src/selectAppTree';
import { startAppColumnSync } from 'src/modules/appColumn';

// This entry chunk is BOOT-excluded and always changes on release, so it's safe for its own bytes
// to carry the version define directly; every other module reads it back via getAppVersion()
// instead of embedding the define itself, which would re-expose it to rolldown folding that module
// into a shared chunk the entry/App exclusion list can't see (generate-app-shared-modules.mjs's
// VERSION_BEARING comment).
// MUST run before either tree calls initSentry(); RootAppNative calls it in a layout effect that
// fires after this top level, and lazy FunnelWeb cannot run before this file has evaluated.
globalThis.__APP_VERSION__ = APP_VERSION;

let nativeBuild: number | undefined;

// The progressive hint is scoped to the native build that wrote it; the gate already reads that build number.
const gatherUpdateInput: typeof gatherUpdateInputRaw = async () => {
  const input = await gatherUpdateInputRaw();
  nativeBuild = Number.isFinite(input.nativeBuildNumber) ? input.nativeBuildNumber : undefined;
  return input;
};

const hintStore = createProgressiveHintStore(
  () => localStorage,
  () => nativeBuild,
);

// The three app trees are mutually exclusive and off the boot-critical path, so each loads lazily.
const App = lazy(() => import('./App'));
const AppWeb = lazy(() => import('src/AppWeb'));
const FunnelWeb = lazy(() => import('src/FunnelWeb'));

// The FR web funnel starts PostHog cookieless until cookie consent.
initPosthog({
  consent: posthogBootConsent(window.location.pathname, {
    isNative: isNative(),
  }),
});

// Register the UDL listener BEFORE initSDK (required by AppsFlyer docs).
registerAppsFlyerDeepLinkListener();

// A lazy chunk that fails to fetch/preload never throws into a React render, so it never reaches
// ErrorBoundary — Vite fires this window event instead.
window.addEventListener('vite:preloadError', (event) => {
  reportChunkFailure({ armRenderProbe, now: Date.now }, event.payload);
});

const cameraRestore = createCameraRestoreTracker({
  isNative: () => isNative(),
  getPlatform: () => getPlatform(),
  now: () => performance.now(),
  schedule: (fn, ms) => setTimeout(fn, ms),
  logAnalyticsEvent,
  reportError,
  setRestoredCameraPhotos,
});

if (isNative()) {
  CapacitorApp.addListener('appRestoredResult', (data) => {
    cameraRestore.onRestoredResult(data as RestoredCameraResult);
  });
}

const unregisterServiceWorkers = (): void => {
  navigator.serviceWorker
    ?.getRegistrations()
    .then((registrations) => {
      registrations.forEach((r) => r.unregister());
    })
    .catch(() => {
      // SW cleanup is best-effort — ignore failures.
    });
};

const APPSFLYER_INIT_TIMEOUT_MS = 3_000;

const scheduleTimer = (fn: () => void, ms: number): (() => void) => {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
};

const RootAppNative = () => {
  const [isReady, setIsReady] = useState(false);
  const [antdLocale, setAntdLocale] = useState<Locale>(enUS);
  const [gate, setGate] = useState<GateState | null>(null);
  const [shouldUpdateNativeApp, setShouldUpdateNativeApp] = useState(false);
  const retryRef = useRef<(() => void) | null>(null);

  const initApp = async () => {
    // First, before any await or early return: every path below (halt, throw, gate) then ends in a
    // release within SPLASH_FALLBACK_MS, so a cover shown before a reload cannot outlive it.
    splashRelease.armFallback();
    try {
      if (
        env.mode === 'production' ||
        env.mode === 'staging'
      ) {
        initSentry();
      }
    } catch {
      // Sentry init must never block the app — continue without error tracking.
    }

    // Deliberately before the fast-track and the update gate: a launch whose own outcome is 'halt'
    // used to skip the late emit.
    emitPendingRevertReport();

    const orphanedSession = detectOrphanedCameraSession();
    if (orphanedSession) {
      const redirectPath = buildCameraRedirectPath(orphanedSession);
      if (redirectPath) {
        setPendingCameraRedirect({
          path: redirectPath,
          lessonId: orphanedSession.lessonId,
          examId: orphanedSession.examId,
        });
      }
    }

    // This fast-track returns before runBootSequence, so it BYPASSES the update GATE — an orphaned
    // camera session boots on whatever bundle is installed, however stale.
    // Deliberate: appRestoredResult is delivered once per launch and gating means reload(), which
    // would lose the restored photo; the next ordinary launch gates as usual.
    if (orphanedSession && isNative()) {
      splashRelease.setBootPath('camera_fast_track');
      const recoveryOutcome = await runCrashRecovery({
        isNative: true,
        now: () => Date.now(),
        schedule: scheduleTimer,
        getFailedUpdate: () => CapacitorUpdater.getFailedUpdate(),
        getCurrentBundleId,
        getCurrentBundleVersion,
        readCrashState,
        recordLaunch,
        clearCrashState,
        clearRenderHealth,
        noteActiveBundleId,
        persistRevertReport,
        readRenderHealth,
        resetToLastSuccessful: () =>
          CapacitorUpdater.reset({ toLastSuccessful: true }),
        resetToBuiltin: () => CapacitorUpdater.reset(),
        notifyAppReady: () => CapacitorUpdater.notifyAppReady(),
        reportError,
      });
      // 'halt' carries the same meaning it has in runBootSequence: a revert is in flight and the
      // webview is being torn down (reset() never settles on success).
      if (recoveryOutcome === 'halt') return;

      // Fire-and-forget: deliberately NOT awaited.
      emitFastTrackUpdateTelemetry({
        gatherUpdateInput,
        setShouldUpdateNativeApp,
        logAnalyticsEvent,
        now: () => Date.now(),
        reportError,
      });

      // Splash stays up until the first route paints or a watchdog/fallback releases it. The flag
      // guards armRenderProbe: arming after a force-render banks a marker only pagehide clears.
      let fastTrackWatchdogFired = false;
      const fastTrackArmedAt = Date.now();
      const disarmFastTrackWatchdog = armBootWatchdog(() => {
        fastTrackWatchdogFired = true;
        const elapsedMs = Date.now() - fastTrackArmedAt;
        reportError(
          'Boot watchdog fired — camera fast-track exceeded BOOT_WATCHDOG_MS',
          { elapsedMs },
        );
        logAnalyticsEvent('app_boot_recovered', { durationMs: elapsedMs });
        splashRelease.release('watchdog');
        setIsReady(true);
      });

      let localInitSucceeded = false;
      try {
        await withTimeout(
          initTranslation(),
          LOCAL_INIT_TIMEOUT_MS,
          'Camera fast-track initTranslation',
        );
        const locale = await withTimeout(
          getAntdLocale(),
          LOCAL_INIT_TIMEOUT_MS,
          'Camera fast-track getAntdLocale',
        );
        setAntdLocale(locale);
        localInitSucceeded = true;
      } catch (error) {
        reportError('Camera-kill fast-track init failed', error);
      }
      initAxios();

      const scope: RestoredPhotoScope = {
        lessonId: orphanedSession.lessonId,
        examId: orphanedSession.examId,
        route: orphanedSession.route,
      };
      cameraRestore.setLateScope(scope);
      await cameraRestore.waitForResult();
      cameraRestore.report(scope);

      runNonCriticalInit({
        initSupport,
        initPushNotifications,
        initAnalytics,
        initInAppPurchase,
        reportError,
      });

      // Guarded on local init succeeding: an unconditional clear wiped the crash counter even when
      // this launch did not boot.
      clearCrashStateIfLocalInitSucceeded(
        { isNative: true, clearCrashState, installBackgroundClear },
        localInitSucceeded,
      );

      try {
        await withTimeout(
          initAppsFlyerSdk(),
          APPSFLYER_INIT_TIMEOUT_MS,
          'Camera fast-track initAppsFlyerSdk',
        );
      } catch (error) {
        reportError('Error initializing AppsFlyer during camera fast-track', error);
      }

      if (!fastTrackWatchdogFired) armRenderProbe(Date.now());
      setIsReady(true);
      startAppsFlyerSdk();
      disarmFastTrackWatchdog();
      return;
    }

    splashRelease.setBootPath('normal');
    void listenForCapgoDownloadFailed((eventName, listener) =>
      CapacitorUpdater.addListener(eventName, listener),
    );
    const outcome = await runBootSequence({
      isNative: isNative(),
      isDev: env.isDev,
      now: () => Date.now(),
      setBootPhase,
      getFailedUpdate: () => CapacitorUpdater.getFailedUpdate(),
      getCurrentBundleId,
      getCurrentBundleVersion,
      readCrashState,
      recordLaunch,
      clearCrashState,
      installBackgroundClear,
      readRenderHealth,
      clearRenderHealth,
      noteActiveBundleId,
      persistRevertReport,
      resetToLastSuccessful: () =>
        CapacitorUpdater.reset({ toLastSuccessful: true }),
      resetToBuiltin: () => CapacitorUpdater.reset(),
      notifyAppReady: () => CapacitorUpdater.notifyAppReady(),
      unregisterServiceWorkers,
      showSplash: () => SplashScreen.showAtBoot(),
      withReloadCover: (operation, source) =>
        SplashScreen.withReloadCover(operation, source),
      liftReloadCover: (reason) => SplashScreen.liftReloadCover(reason),
      hideSplash: async () => splashRelease.release('gate'),
      nextPaint: () =>
        new Promise<void>((resolve) => requestAnimationFrame(() => resolve())),
      runLocalInit: () => runLocalInit(setAntdLocale),
      gatherUpdateInput,
      isProgressiveTrigger: makeIsProgressiveTrigger(isIos),
      cleanupFailedBundles: isIos() ? makeCleanupFailedBundles(CapacitorUpdater) : undefined,
      readProgressiveHint: hintStore.read,
      writeProgressiveHint: hintStore.write,
      addDownloadListener: async (onPercent) => {
        const listener = await CapacitorUpdater.addListener(
          'download',
          ({ percent }) => onPercent(percent),
        );
        return () => listener.remove();
      },
      download: (o) => CapacitorUpdater.download(o),
      set: (o) => CapacitorUpdater.set(o),
      // eslint-disable-next-line no-restricted-syntax -- only the gate's retry actions reload alone and bootSequence covers those; the set() cover carries an apply
      reload: () => CapacitorUpdater.reload(),
      getMissingBundleFiles: (o) =>
        CapacitorUpdater.getMissingBundleFiles(o) as unknown as ReturnType<
          BootSequenceDeps['getMissingBundleFiles']
        >,
      readDeviceDiagnostics,
      watchConnectionType: async (onChange) => {
        const handle = await Network.addListener('networkStatusChange', (status) => {
          const type = toConnectionType(status.connectionType);
          if (type) onChange(type);
        });
        return () => handle.remove();
      },
      schedule: scheduleTimer,
      setRetryAction: (action) => {
        retryRef.current = action;
      },
      setShouldUpdateNativeApp,
      setGate,
      reportError,
      logAnalyticsEvent,
    });

    // 'halt' means a reload/reset is in flight, or the update gate owns the screen. Either way this
    // bundle must not go on to render.
    if (outcome === 'halt') return;

    // Armed here, not earlier (it would force-render past the update gate's download); never reverts.
    // watchdogFired: arming render-health after a force-render banks a marker only pagehide clears.
    let watchdogFired = false;
    const bootWatchdogArmedAt = Date.now();
    const disarmBootWatchdog = armBootWatchdog(() => {
      watchdogFired = true;
      const elapsedMs = Date.now() - bootWatchdogArmedAt;
      reportError('Boot watchdog fired — boot tail exceeded BOOT_WATCHDOG_MS', {
        elapsedMs,
      });
      logAnalyticsEvent('app_boot_recovered', { durationMs: elapsedMs });
      splashRelease.release('watchdog');
      setIsReady(true);
    });

    if (cameraRestore.hasResult()) {
      cameraRestore.report(
        orphanedSession
          ? {
              lessonId: orphanedSession.lessonId,
              examId: orphanedSession.examId,
              route: orphanedSession.route,
            }
          : null,
      );
    }

    runNonCriticalInit({
      initSupport,
      initPushNotifications,
      initAnalytics,
      initInAppPurchase,
      reportError,
    });

    if (isAndroid()) {
      SafeArea.setSystemBarsStyle({
        style: SystemBarsStyle.Light,
        type: undefined,
      }).catch((error) => {
        reportError('Error setting system bars style', error);
      });
    }

    const hostname = window.location.hostname;
    const isFirebasePreviewHost =
      hostname.includes('--pr') && hostname.endsWith('.web.app');

    // Firebase preview channels are frequently redeployed and are prone to stale SW caches. Skip SW
    // registration there to keep asset resolution tied to the latest deployment.
    if (!isFirebasePreviewHost && !isNative()) {
      registerSW({
        onRegistered(r) {
          r &&
            setInterval(
              () => {
                r.update();
              },
              60 * 60 * 1000,
            );
        },
      });
    }

    await runPostRenderTail({
      platform: getPlatform(),
      requestAtt: requestATTPermission,
      enableFacebookTrackingIfUnconfigured,
      initAppsFlyerSdk,
      startAppsFlyerSdk,
      setBootPhase,
      isWatchdogFired: () => watchdogFired,
      armRenderProbe,
      now: () => Date.now(),
      setIsReady,
      disarmBootWatchdog,
      reportError,
    });
  };

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    initApp();

    const handleLanguageChange = async () => {
      const locale = await getAntdLocale();
      setAntdLocale(locale);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    resetLegacyAppIcon();
  }, []);

  const view = pickBootView({ gate, isReady });

  if (view.kind === 'gate') {
    return (
      <UpdateGate
        percent={view.gate.percent}
        isOffline={view.gate.isOffline}
        hasProgress={view.gate.hasProgress}
        hint={view.gate.hint}
        locale={resolveGateLocale(i18n?.language, navigator.language)}
        onRetry={() => retryRef.current?.()}
      />
    );
  }

  if (view.kind === 'blank') {
    return <BootPlaceholder />;
  }

  return (
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ConfigProvider theme={antdTheme} locale={antdLocale}>
            <AntApp>
              <NotificationContextProvider>
                <BootHealthGate>
                  <Suspense fallback={<BootPlaceholder spinner />}>
                    <App shouldUpdateNativeApp={shouldUpdateNativeApp} />
                  </Suspense>
                  <PreviewBadge />
                  <TikTokPixel />
                  <AppsFlyer />
                  {env.mode === 'staging' && <DebugShakeListener />}
                </BootHealthGate>
              </NotificationContextProvider>
            </AntApp>
          </ConfigProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
};

const appTree = selectAppTree();

const Root = () => {
  if (appTree === 'funnel') return <FunnelWeb />;
  if (appTree === 'native') return <RootAppNative />;
  return <AppWeb />;
};

// A hot update reaching this module re-runs it; a second createRoot() on the same container would
// mount a duplicate tree.
const reactAppContainer = document.getElementById('react-app');
if (!reactAppContainer) throw new Error('Missing #react-app container');
// Every error boundary in the app reports through this root option; componentDidCatch does not.
const root =
  (import.meta.hot?.data.root as ReactDOM.Root | undefined) ??
  ReactDOM.createRoot(reactAppContainer, { onCaughtError: onReactCaughtError });
if (import.meta.hot) import.meta.hot.data.root = root;
if (appTree !== 'funnel') {
  startAppColumnSync();
}
if (appTree === 'web') redirectReferralVisitToStore();
root.render(
  <PostHogProvider client={posthog}>
    <PostHogErrorBoundary>
      <Suspense fallback={<PageSpinner />}>
        <Root />
      </Suspense>
    </PostHogErrorBoundary>
  </PostHogProvider>,
);
